import { isSupabaseConfigured, supabase } from './supabaseClient';

class AuthService {
    constructor() {
        this.storagePrefix = 'financer_auth_';
        this.currentUser = JSON.parse(localStorage.getItem(this.storagePrefix + 'currentUser')) || null;
    }

    async init() {
        if (!isSupabaseConfigured) return this.currentUser;

        const { data, error } = await supabase.auth.getSession();
        if (error || !data.session?.user) {
            this.currentUser = null;
            localStorage.removeItem(this.storagePrefix + 'currentUser');
            return null;
        }

        this.currentUser = this.mapSupabaseUser(data.session.user);
        localStorage.setItem(this.storagePrefix + 'currentUser', JSON.stringify(this.currentUser));
        return this.currentUser;
    }

    mapSupabaseUser(user) {
        return {
            id: user.id,
            name: user.user_metadata?.name || user.email,
            email: user.email
        };
    }

    // Получение списка пользователей
    getUsers() {
        return JSON.parse(localStorage.getItem(this.storagePrefix + 'users')) || [];
    }

    // Регистрация нового пользователя
    async register(name, email, password) {
        if (isSupabaseConfigured) {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { name }
                }
            });

            if (error) throw new Error(error.message);
            if (!data.session) {
                this.currentUser = null;
                localStorage.removeItem(this.storagePrefix + 'currentUser');
                throw new Error('Аккаунт создан. Подтверди email, затем войди в систему.');
            }

            if (data.user) {
                this.currentUser = this.mapSupabaseUser(data.user);
                localStorage.setItem(this.storagePrefix + 'currentUser', JSON.stringify(this.currentUser));
            }
            return this.currentUser;
        }

        const users = this.getUsers();
        
        // Проверка существующего пользователя
        if (users.find(u => u.email === email)) {
            throw new Error('Пользователь с такой почтой уже существует');
        }
        if (users.find(u => u.name === name)) {
            throw new Error('Пользователь с таким именем уже существует');
        }

        const newUser = {
            id: Date.now(),
            name,
            email,
            password // В реальном приложении пароль нужно хешировать
        };

        users.push(newUser);
        localStorage.setItem(this.storagePrefix + 'users', JSON.stringify(users));
        
        await this.login(name, password);
        return newUser;
    }

    // Вход в систему
    async login(name, password) {
        if (isSupabaseConfigured) {
            const { data, error } = await supabase.auth.signInWithPassword({
                email: name,
                password
            });

            if (error) throw new Error(error.message);
            this.currentUser = this.mapSupabaseUser(data.user);
            localStorage.setItem(this.storagePrefix + 'currentUser', JSON.stringify(this.currentUser));
            return this.currentUser;
        }

        const users = this.getUsers();
        const user = users.find(u => u.name === name && u.password === password);
        
        if (!user) {
            throw new Error('Неверное имя пользователя или пароль');
        }

        const userData = { id: user.id, name: user.name, email: user.email };
        localStorage.setItem(this.storagePrefix + 'currentUser', JSON.stringify(userData));
        this.currentUser = userData;
        return userData;
    }

    // Выход из системы
    async logout() {
        if (isSupabaseConfigured) {
            await supabase.auth.signOut();
        }

        localStorage.removeItem(this.storagePrefix + 'currentUser');
        this.currentUser = null;
    }

    // Получение текущего пользователя
    getCurrentUser() {
        return this.currentUser;
    }

    // Проверка аутентификации
    isAuthenticated() {
        return this.currentUser !== null;
    }
}

const auth = new AuthService();
export default auth;
